export interface Catalog {
    readonly titles: {
        readonly [titleId: string]: CatalogTitle;
    };
    readonly games: {
        readonly [gameId: string]: CatalogGame;
    };
    readonly creators: {
        readonly [creatorId: string]: CatalogGameCreator;
    };
    readonly genres: {
        readonly [genreId: string]: CatalogGenre;
    };
    readonly platforms: {
        readonly [platformId: string]: object;
    };
    readonly maps: readonly CatalogGameMap[];
    readonly queue: readonly string[]; // game ids
    readonly stats: CatalogStats;
    readonly featured: readonly CatalogFeaturedContent[];
    readonly announcements: readonly CatalogAnnouncement[];
    readonly polls: readonly CatalogPoll[];
    readonly releaseChannels: readonly CatalogReleaseChannel[];
    readonly featuredGames: readonly CatalogFeaturedGameContent[];
    readonly featuredRail: readonly CatalogFeaturedRailItem[];
    readonly searchBarAds?: readonly CatalogSearchBarAd[];
    readonly features: {
        readonly [featureKey: string]: object;
    };
}

const isObject = (v: unknown): boolean => typeof v === 'object' && v !== null;

const catalogKeys: [keyof Catalog, (v: unknown) => boolean][] = [
    ['titles', isObject],
    ['games', isObject],
    ['creators', isObject],
    ['genres', isObject],
    ['platforms', isObject],
    ['maps', Array.isArray],
    ['queue', Array.isArray],
    ['stats', isObject],
    ['featured', Array.isArray],
    ['announcements', Array.isArray],
    ['polls', Array.isArray],
    ['releaseChannels', Array.isArray],
    ['featuredGames', Array.isArray],
    ['featuredRail', Array.isArray],
];

export function validateCatalog(catalog: never): boolean {
    return catalog === null || (isObject(catalog) && catalogKeys.every(entry => entry[1](catalog[entry[0]])));
}

export interface CatalogTitle {
    readonly id: string;
    readonly slug: string;
    readonly name: string;
    readonly terms: readonly string[];
    readonly thumbnail: string;
    readonly genreIds: readonly string[];
    readonly gameIds: readonly string[];
    readonly flags?: CatalogTitleFlags;
}

export enum CatalogTitleFlags {
    HasAssistant = 1 << 0,
    IsVerifiedDeveloper = 1 << 1,
}

export interface CatalogGenre {
    readonly id: string;
    readonly slug: string;
    readonly name: string;
}

export type WellKnownGameTag = 'free' | 'game-pass' | 'choice';

export interface CatalogGame {
    readonly id: string;
    readonly titleId: string;
    readonly platformId: string;
    readonly correlationIds: readonly string[];
    readonly purchaseUris: readonly string[];
    readonly tags: readonly (WellKnownGameTag | string)[];
    readonly flags: CatalogGameFlags;
    readonly creatorId?: string;
    readonly edition?: string;
    readonly versionPath?: string;
    readonly trainer?: CatalogAvailableTrainer;
}

export enum CatalogGameFlags {
    ReleaseQueued = 1 << 0,
    UpdateQueued = 1 << 1,
    Active = 1 << 2,
    Retired = 1 << 3,
    Unsupported = 1 << 4,
    Outdated = 1 << 5,
    Free = 1 << 6,
    AllowCheatSuggestions = 1 << 7,
    Queued = UpdateQueued | ReleaseQueued,
    Available = Active | Retired,
    OverlaySupported = 1 << 8,
    PrecisionModsSupported = 1 << 9,
}

export enum CatalogGameMapFlags {
    HasGameCoordinates = 1 << 0,
    SupportsLiveLocation = 1 << 1,
    SupportsTeleport = 1 << 2,
}

export interface CatalogGameCreator {
    readonly id: string;
    readonly username: string;
    readonly avatar: string;
    readonly active: boolean;
}

export interface CatalogGameMap {
    readonly id: string;
    readonly titleId: string;
    readonly name: string;
    readonly thumbnail: string;
    readonly flags?: CatalogGameMapFlags;
    readonly supportsTeleport?: boolean;
}

export interface CatalogFeaturedGameContent {
    readonly caption: string;
    readonly image: string;
    readonly titleId: string | null;
    readonly sponsored: boolean;
    readonly url?: string | null;
}

export interface CatalogFeaturedRailItem extends CatalogTargetableContent {
    readonly id: string;
    readonly sponsored: boolean;
    readonly caption: string | null;
    readonly image: string | null;
    readonly url: string;
    readonly campaignId?: string;
    readonly sponsor?: {
        readonly network?: string | null;
        readonly trackingUrl?: string | null;
    };
}

export interface CatalogSearchBarAd extends CatalogTargetableContent {
    readonly id: string;
    readonly title: string | null;
    readonly icon: string | null;
    readonly image: string;
    readonly url: string;
    readonly campaignId: string;
    readonly sponsor: {
        readonly network?: string | null;
        readonly trackingUrl?: string | null;
    };
}

export interface CatalogAvailableTrainer {
    readonly cheatCount: number;
    readonly players: number;
    readonly rank: number;
    /** Rank percentile of trailing-7-day active users among all active games, in [0, 1] — not a real count. */
    readonly weeklyActivityScore: number;
    readonly supportedVersions: readonly number[];
    readonly discussionUrl: string | null;
    readonly videoUrl: string | null;
    readonly contributorIds: readonly string[];
    readonly createdAt: string; // first trainer release time
    readonly updatedAt: string; // last trainer release time
}

export interface CatalogStats {
    readonly weeklyGamesAdded: number;
    readonly weeklyGamesUpdated: number;
    readonly monthlyGamesAdded: number;
    readonly monthlyGamesUpdated: number;
    readonly trustpilotReviews: number;
}

export interface CatalogTargetableContent {
    readonly audience?: CatalogAudience; // deprecated
    readonly target?: CatalogContentTarget;
    readonly excludedRegions?: readonly string[];
    readonly excludedInstalledApps?: readonly string[];
}

export interface CatalogContentTarget {
    readonly audience?: CatalogAudience;
    readonly countries?: readonly string[];
    readonly languages?: readonly string[];
    readonly maxOsVersion?: string;
    readonly minOsVersion?: string;
}

export type CatalogAudience = number;

export interface CatalogReleaseChannel extends CatalogTargetableContent {
    readonly id: string;
    readonly name: string;
    readonly feedUrl: string;
    readonly config: {
        readonly debug?: true;
    };
}

export interface CatalogFeaturedContent extends CatalogTargetableContent {
    readonly id: string;
    readonly title: string;
    readonly subtitle: string | null;
    readonly image: string;
    readonly overlay: string | null;
    readonly url: string | null;
    readonly badgeType: CatalogFeaturedContentBadgeType;
    readonly badgeText: string;
    readonly badgeColor: string;
}

export type CatalogFeaturedContentBadgeType = 'text' | 'epic_gotw' | 'community_choice';

export interface CatalogAnnouncement extends CatalogTargetableContent {
    readonly type: CatalogAnnouncementType;
    readonly message: string;
    readonly url: string | null;
}

export type CatalogAnnouncementType = 'info' | 'alert';

export interface CatalogPoll extends CatalogTargetableContent {
    readonly id: string;
    readonly usage: CatalogPollUsage;
    readonly title: string;
    readonly message?: string;
    readonly options: readonly string[];
    readonly multiselect?: {
        readonly minSelections?: number;
        readonly maxSelections?: number;
    };
    readonly customSelection?: {
        // always attached to the last option
        readonly placeholder?: string;
        readonly maxLength: number;
    };
    readonly detailsField?: {
        readonly lastOptionOnly?: boolean;
        readonly placeholder: string;
        readonly minLength?: number; // falsy means optional
        readonly maxLength: number;
    };
    readonly startedAt: string;
}

export type CatalogPollUsage = 'generic' | 'subscription_cancelation';
